import { observable, computed, action, decorate } from 'mobx'
import storageManager from '../helpers/storageManager'
import { redirectToLogout } from '../helpers/routingHelpers'
import { notify } from '../helpers/generalHelpers'
import { Logger } from '../services/logger'
import { services } from 'api/api'
import settings from 'settings/index'

const sessionTokenLocalStorageKey = 'session'

const { ACTIVE_WORKSPACE_STORAGE_KEY } = settings

const unAuthenticated = {
  token: null,
}

const authDefaults = unAuthenticated

class mainStore {
  //
  // ─── AUTH ───────────────────────────────────────────────────────────────────────
  //

  auth = authDefaults

  sessionToken = ''

  isFormMakerSaving = false

  setPrevAuthData = (prevAuth) => {
    this.auth = prevAuth
  }
  setPrevSessionData = (session) => {
    this.sessionToken = session
  }

  persistSessionData = () => {
    storageManager.write(sessionTokenLocalStorageKey, this.sessionToken)
  }

  removeAuthData = () => {
    storageManager.remove(sessionTokenLocalStorageKey, this.auth)
    storageManager.remove(ACTIVE_WORKSPACE_STORAGE_KEY)
  }

  get isAuthenticated() {
    return Boolean(this.sessionToken)
  }

  get token() {
    return this.auth.token
  }

  //
  // ─── DRAG AND DROP ──────────────────────────────────────────────────────────────
  //

  isDragging = false

  dragStart = () => {
    this.isDragging = true
  }

  dragEnd = () => {
    this.isDragging = false
  }

  get isDragHappening() {
    return this.isDragging
  }

  //
  // ─── TASK MANAGEMENT ────────────────────────────────────────────────────────────
  //

  isAllowedToEndSaving = true
  tasksQueue = []

  enqueue = (fn) => {
    this.tasksQueue.push(fn)
  }

  runEnqueuedTasks = () => {
    this.tasksQueue.forEach((fn) => {
      fn()
    })
  }

  formMakerIsSavingStart = () => {
    this.isAllowedToEndSaving = false
    this.isFormMakerSaving = true

    // release the lock after a delay
    setTimeout(() => {
      this.isAllowedToEndSaving = true
      this.runEnqueuedTasks()
    }, 500)
  }

  formMakerIsSavingEnd = () => {
    if (this.isAllowedToEndSaving) {
      this.isFormMakerSaving = false
    } else {
      this.enqueue(this.formMakerIsSavingEnd)
    }
  }

  //
  // ─── PROFILE ────────────────────────────────────────────────────────────────────
  //

  setUserToken = (tokenObj) => {
    this.auth = tokenObj

    // this.persistAuthData()
  }

  setSessionToken = (token) => {
    this.sessionToken = token

    this.persistSessionData()
  }

  logout = () => {
    this.auth = authDefaults
    this.removeAuthData()
    Logger.clearUser()
    redirectToLogout()
  }

  get fullName() {
    return (this.auth.first_name || '') + ' ' + (this.auth.last_name || '')
  }

  //
  // ─── FORM TEMPLATES ─────────────────────────────────────────────────────────────
  //

  formTemplates = []

  addLoadedTemplates = (templates) => {
    this.formTemplates = templates
  }

  //
  // ─── MY FORMS ───────────────────────────────────────────────────────────────────
  //

  myForms = []

  addLoadedMyForms = (forms) => {
    this.myForms = forms
  }

  get userHasForms() {
    return Boolean(this.myForms.length > 0)
  }

  /* user categories */
  userCategories = []

  fetchCategories = (categories) => {
    this.userCategories = categories
  }
}

decorate(mainStore, {
  auth: observable,
  sessionToken: observable,
  isFormMakerSaving: observable,
  setPrevAuthData: observable,
  setPrevSessionData: observable,
  isDragging: observable,
  formTemplates: observable,
  myForms: observable,
  userCategories: observable,
  isAuthenticated: computed,
  token: computed,
  isDragHappening: computed,
  fullName: computed,
  userHasForms: computed,
  dragStart: action,
  dragEnd: action,
  formMakerIsSavingStart: action,
  formMakerIsSavingEnd: action,
  setUserToken: action,
  setSessionToken: action,
  logout: action,
  addLoadedTemplates: action,
  addLoadedMyForms: action,
  fetchCategories: action,
})

const store = new mainStore()
export default store

export const loginIfPrevAuthFound = () => {
  const prevSession = storageManager.read(sessionTokenLocalStorageKey)

  if (prevSession) {
    store.setPrevSessionData(prevSession)
  }
}

export const authPredicate = () => store.isAuthenticated

export async function getOrRefreshToken() {
  const {
    data: { errors, token },
  } = await services.auth.getToken()

  if (errors) {
    notify(errors.general_errors, 'error')
    store.logout()
    return Promise.reject(errors)
  }

  if (token) {
    store.setUserToken({ token })
    return Promise.resolve(token)
  }
}
